import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { cn, extractErrorMessage } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { FeedbackType, FeedbackPriority } from "@/shared/schemas/feedback.schema";
import {
  FEEDBACK_TYPES,
  FEEDBACK_TYPE_LABELS,
  FEEDBACK_PRIORITIES,
  FEEDBACK_PRIORITY_LABELS,
} from "@/shared/schemas/feedback.schema";
import { FEEDBACK_DESCRIPTION_HINT, FEEDBACK_MAX_IMAGES } from "@/shared/schemas/feedbackStatus";
import {
  FEEDBACK_SHEET_BODY_CLASS,
  FEEDBACK_SHEET_CONTENT_CLASS,
  FEEDBACK_SHEET_FOOTER_CLASS,
  FEEDBACK_SHEET_HEADER_CLASS,
  FeedbackImagePickerButtons,
  FeedbackThumbGrid,
  parseFeedbackImages,
  uploadFeedbackImages,
} from "@/components/feedback/feedback-ui";
import {
  getDiagnosticsSnapshot,
  getDiagnosticsSummaryText,
  getSuggestedTitleFromDiagnostics,
  getCurrentAppVersion,
  type DiagnosticsSnapshot,
} from "@/lib/diagnostics";

interface CreateFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any | null;
}

const PRIORITY_SELECTED: Record<FeedbackPriority, string> = {
  CRITICAL: "bg-red-600 border-red-600 text-white",
  HIGH: "bg-orange-500 border-orange-500 text-white",
  MEDIUM: "bg-blue-600 border-blue-600 text-white",
  LOW: "bg-slate-500 border-slate-500 text-white",
};

export function CreateFeedbackDialog({
  open,
  onOpenChange,
  initialData,
}: CreateFeedbackDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isEditMode = Boolean(initialData?.id);

  const [type, setType] = useState<FeedbackType>("BUG");
  const [priority, setPriority] = useState<FeedbackPriority>("MEDIUM");
  const [moduleName, setModuleName] = useState("Hệ thống chung");
  const [pageUrl, setPageUrl] = useState("/");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [attachDiagnostics, setAttachDiagnostics] = useState(true);
  const [diagSnapshot, setDiagSnapshot] = useState<DiagnosticsSnapshot | null>(null);

  useEffect(() => {
    if (!open) return;
    const snap = getDiagnosticsSnapshot();
    setDiagSnapshot(snap);
    setAttachDiagnostics(true);
    if (initialData?.id) {
      setType((initialData.type as FeedbackType) || "BUG");
      setPriority((initialData.priority as FeedbackPriority) || "MEDIUM");
      setModuleName(initialData.module || "Hệ thống chung");
      setPageUrl(initialData.pageUrl || "/");
      setTitle(initialData.title || "");
      setDescription(initialData.description || "");
      setImages(
        Array.from(
          new Set(
            [
              ...(initialData.screenshotBefore ? [initialData.screenshotBefore] : []),
              ...parseFeedbackImages(initialData.images),
            ].filter(Boolean),
          ),
        ),
      );
    } else {
      const suggested = getSuggestedTitleFromDiagnostics(snap);
      setTitle(suggested || "");
      setDescription("");
      setType("BUG");
      setPriority("MEDIUM");
      setModuleName("Hệ thống chung");
      setPageUrl(typeof window !== "undefined" ? window.location.pathname : "/");
      setImages([]);
    }
  }, [open, initialData]);

  const createMutation = useMutation({
    mutationFn: async (vars: {
      type: FeedbackType;
      priority: FeedbackPriority;
      module: string;
      pageUrl: string;
      title: string;
      description: string;
      images: string[];
      source?: "USER";
      diagnostics?: unknown;
      appVersion?: string;
      userAgent?: string;
      sessionId?: string;
    }) => trpcClient.feedback.create.mutate(vars as any),
    onSuccess: (data: any) => {
      toast.success(`Đã gửi phản hồi #${data?.code || ""}`, {
        action: data?.id
          ? { label: "Mở phiếu", onClick: () => navigate({ to: `/feedback/${data.id}` as string }) }
          : undefined,
      });
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["feedbacks"] });
    },
    onError: (err: any) => {
      toast.error("Không thể gửi phản hồi: " + extractErrorMessage(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (vars: Record<string, unknown>) => trpcClient.feedback.update.mutate(vars as any),
    onSuccess: () => {
      toast.success(`Đã cập nhật phiếu #${initialData?.code || ""}`);
      onOpenChange(false);
      if (initialData?.id) {
        queryClient.invalidateQueries({ queryKey: ["feedback", initialData.id] });
      }
      queryClient.invalidateQueries({ queryKey: ["feedbacks"] });
    },
    onError: (err: any) => {
      toast.error("Không thể cập nhật phiếu: " + extractErrorMessage(err));
    },
  });

  const addFiles = useCallback(
    async (files: File[]) => {
      setIsUploading(true);
      try {
        const urls = await uploadFeedbackImages(files, FEEDBACK_MAX_IMAGES - images.length, "Ảnh phản hồi");
        if (urls.length) setImages((prev) => [...prev, ...urls].slice(0, FEEDBACK_MAX_IMAGES));
      } finally {
        setIsUploading(false);
      }
    },
    [images.length],
  );

  const handlePaste = useCallback(
    async (e: React.ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.items || [])
        .filter((it) => it.type.startsWith("image/"))
        .map((it) => it.getAsFile())
        .filter((f): f is File => !!f);
      if (files.length) {
        e.preventDefault();
        await addFiles(files);
      }
    },
    [addFiles],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      const files = Array.from(e.dataTransfer?.files || []);
      if (!files.length) return;
      e.preventDefault();
      await addFiles(files);
    },
    [addFiles],
  );

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error("Vui lòng nhập tiêu đề");
      return;
    }
    const payload = {
      type,
      priority,
      module: moduleName.trim() || "Hệ thống chung",
      pageUrl: pageUrl.trim() || "/",
      title: title.trim(),
      description: description.trim(),
      images,
    };
    if (isEditMode && initialData?.id) {
      updateMutation.mutate({ id: initialData.id, ...payload, screenshotBefore: images[0] || null });
      return;
    }
    const latestSnap = attachDiagnostics ? getDiagnosticsSnapshot() : undefined;
    createMutation.mutate({
      ...payload,
      source: "USER",
      diagnostics: latestSnap,
      sessionId: latestSnap?.sessionId,
      appVersion: getCurrentAppVersion(),
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    });
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const chipBtn =
    "min-h-11 sm:min-h-9 px-3 rounded-lg border text-sm font-medium transition-colors cursor-pointer";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(FEEDBACK_SHEET_CONTENT_CLASS, "sm:max-w-xl")}
        onPaste={handlePaste}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        data-testid="fb-create-dialog"
      >
        <DialogHeader className={FEEDBACK_SHEET_HEADER_CLASS}>
          <DialogTitle className="text-base font-bold text-slate-800 pr-8">
            {isEditMode ? `Sửa phiếu #${initialData?.code || ""}` : "Gửi phản hồi"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {isEditMode ? "Cập nhật nội dung và ảnh của phiếu." : "Báo lỗi, góp ý cải tiến hoặc đặt câu hỏi."}
          </DialogDescription>
        </DialogHeader>

        <div className={FEEDBACK_SHEET_BODY_CLASS}>
          <div className="space-y-1.5">
            <Label htmlFor="fb-title" className="text-sm font-semibold text-slate-700">
              Tiêu đề <span className="text-red-500">*</span>
            </Label>
            <Input
              id="fb-title"
              data-testid="fb-title-input"
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Tóm tắt ngắn gọn vấn đề"
              className="h-11 sm:h-9 text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Loại</Label>
            <div className="flex flex-wrap gap-2">
              {FEEDBACK_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`fb-type-${t}`}
                  aria-pressed={type === t}
                  onClick={() => setType(t)}
                  className={cn(
                    chipBtn,
                    type === t
                      ? "bg-blue-50 border-blue-500 text-blue-700 font-semibold"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50",
                  )}
                >
                  {FEEDBACK_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Mức độ</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {FEEDBACK_PRIORITIES.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`fb-priority-${p}`}
                  aria-pressed={priority === p}
                  onClick={() => setPriority(p)}
                  className={cn(
                    chipBtn,
                    priority === p
                      ? PRIORITY_SELECTED[p]
                      : "border-slate-200 text-slate-600 hover:bg-slate-50",
                  )}
                >
                  {FEEDBACK_PRIORITY_LABELS[p]}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="fb-desc" className="text-sm font-semibold text-slate-700">Mô tả</Label>
            <Textarea
              id="fb-desc"
              data-testid="fb-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={FEEDBACK_DESCRIPTION_HINT}
              className="min-h-[110px] text-sm resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 min-w-0">
              <Label htmlFor="fb-module" className="text-sm font-semibold text-slate-700">Module</Label>
              <Input
                id="fb-module"
                data-testid="fb-module-input"
                value={moduleName}
                onChange={(e) => setModuleName(e.target.value)}
                className="h-11 sm:h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5 min-w-0">
              <Label htmlFor="fb-page" className="text-sm font-semibold text-slate-700">Trang</Label>
              <Input
                id="fb-page"
                data-testid="fb-page-input"
                value={pageUrl}
                onChange={(e) => setPageUrl(e.target.value)}
                className="h-11 sm:h-9 text-sm"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">
              Ảnh ({images.length}/{FEEDBACK_MAX_IMAGES})
            </Label>
            <FeedbackImagePickerButtons
              testIdPrefix="fb-create"
              onFiles={addFiles}
              isUploading={isUploading}
              disabled={isUploading || images.length >= FEEDBACK_MAX_IMAGES}
            />
            <FeedbackThumbGrid
              testId="fb-create-thumbs"
              images={images}
              isUploading={isUploading}
              badgeFirst="Ảnh bìa"
              onRemove={(idx) => setImages((prev) => prev.filter((_, i) => i !== idx))}
            />
            <p className="hidden sm:block text-xs text-slate-400">Có thể dán ảnh (Ctrl+V) hoặc kéo thả ảnh vào đây.</p>
          </div>

          {!isEditMode && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="fb-create-attach-diagnostics"
                  checked={attachDiagnostics}
                  onChange={(e) => setAttachDiagnostics(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-700">
                    Đính kèm thông tin chẩn đoán
                  </div>
                  <div
                    data-testid="fb-create-diagnostics-summary"
                    className="text-xs text-slate-500 mt-0.5 break-words whitespace-normal"
                  >
                    {getDiagnosticsSummaryText(diagSnapshot || undefined)}
                  </div>
                </div>
              </label>
            </div>
          )}
        </div>

        <div className={FEEDBACK_SHEET_FOOTER_CLASS}>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving || isUploading}
            className="min-h-11 sm:min-h-9 flex-1 sm:flex-none"
          >
            Hủy
          </Button>
          <Button
            type="button"
            data-testid="btn-submit-feedback-dialog"
            onClick={handleSubmit}
            disabled={!title.trim() || isSaving || isUploading}
            className="min-h-11 sm:min-h-9 flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isEditMode ? "Đang lưu..." : "Đang gửi..."}
              </>
            ) : isEditMode ? (
              "Lưu thay đổi"
            ) : (
              "Gửi phản hồi"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
